import { format, isDate } from 'date-fns';
import React from 'react';

export const displayDateTime = (dateValOrString: Date | string | null) => {
  if (!dateValOrString) return '';

  const date = typeof dateValOrString === 'string' ? new Date(dateValOrString) : dateValOrString;

  if (isNaN(date.getTime())) return '';

  return format(date, 'MM/dd/yyyy hh:mm a');
};

export const displayDateOnly = (dateValOrString: Date | null) => {
  let output;
  let dateString: string;
  const dateFormat = RegExp(/\d{2}\/\d{2}\/\d{4}/);
  const dateFormat2 = RegExp(/\d{4}-\d{2}-\d{2}/);
  if (dateValOrString === null) {
    return '';
  } else if (isDate(dateValOrString) && dateValOrString instanceof Date) {
    /// 2000-01-01T05:00:00.000Z
    dateString = format(
      dateValOrString as unknown as Date | number,
      "yyyy-MM-dd'T'HH:mm:ss"
    );
  } else {
    dateString = dateValOrString as string;
  }

  if (!dateString) {
    output = '';
    return output;
  }

  if (dateString.includes('T')) {
    const [datePart, ...rest] = dateString.split('T');
    const [year, month, day] = datePart.split('-');
    output = `${month}/${day}/${year}`;
    return output;
  }

  if (dateFormat.test(dateString)) {
    output = dateString;
    return output;
  } else if (dateFormat2.test(dateString)) {
    const [year, month, day] = dateString.split('-');
    output = `${month}/${day}/${year}`;
    return output;
  }
  return output;
};

export const formatDateTime12h = (value: Date | string | null, dateFormat = 'MM-dd-yyyy') => {
  if (!value) return '';
  const d = typeof value === 'string' ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return '';
  return format(d, `${dateFormat} hh:mm a`);
};

export const convertTZ = (date: Date, tzString: string) => {
  return new Date(
    (typeof date === 'string' ? new Date(date) : date).toLocaleString('en-US', {
      timeZone: tzString,
    })
  );
};

export const formatDateInputs = (value: string) => {
  const dateFormat2 = RegExp(/\d{4}-\d{2}-\d{2}/);
  let input = value || '';
  if (input === '') return '';

  if (input && input.includes('T')) {
    const [dParts, tParts] = input.split('T');
    const [year, month, day] = dParts.split('-');
    input = `${day}/${month}/${year}`;
    // input = `${year}/${month}/${day}`;
  } else {
    const [year, month, day] = input.split('-');
    input = `${day}/${month}/${year}`;
  }

  input = input.replace(/\D/g, '');

  input = input.substring(0, 12);
  const size = input.length;

  if (size >= 8) {
    // return `${input.substring(0, 2)}-${input.substring(2, 4)}-${input.substring(4)}`;
    const test = `${input.substring(4, 8)}-${input.substring(
      2,
      4
    )}-${input.substring(0, 2)}`;
    return `${input.substring(4, 8)}-${input.substring(2, 4)}-${input.substring(
      0,
      2
    )}`;
  }
};

export const isNumber = (value: any) => {
  return typeof value === 'number';
};

export const onlyNumberInputRaw = (
  event: React.KeyboardEvent<HTMLInputElement>
) => {
  const key = event.key;
  if (
    key &&
    key !== 'Backspace' &&
    key !== 'Delete' &&
    key !== 'Home' &&
    key !== 'End' &&
    key !== 'Tab' &&
    key !== 'ArrowLeft' &&
    key !== 'ArrowRight' &&
    !/[0-9]/.test(event.key)
  ) {
    return true;
  }
  return false;
};

export const onlyNumberInput = (
  event: React.KeyboardEvent<HTMLInputElement>
) => {
  const key = event.key;
  if (
    key &&
    key !== 'Backspace' &&
    key !== 'Tab' &&
    key !== 'ArrowLeft' &&
    key !== 'ArrowRight' &&
    key !== '.' &&
    !/[0-9]/.test(event.key)
  ) {
    return true;
  }
  return false;
};

export const cleanMoney = (value: string) =>
  value.length > 0 ? value.replace(/^(-)|[^0-9.]+/g, '$1') : value ?? '';

export const formatNumbers = (value: any) => {
  if (!value) {
    return value;
  }
  const hasSeparator = `${value}`.includes('.');
  const [before, after] = `${value}`.split('.');

  let formattedBefore = '';
  let formattedAfter = '';

  if (before !== '-') {
    formattedBefore = new Intl.NumberFormat().format(+cleanMoney(before));
  }
  if (before === '-') {
    formattedBefore = before;
  }
  if (after !== '' && !isNaN(+after)) {
    let afterFormatted = after;
    if (after.length > 2) {
      afterFormatted = afterFormatted.slice(0, 2);
    }
    const afterString = '0.' + afterFormatted;
    formattedAfter = afterString;
  }
  if (formattedAfter) {
    return `${formattedBefore}${formattedAfter.slice(1)}`;
  }
  if (hasSeparator) {
    return `${formattedBefore}.`;
  }
  return formattedBefore ?? '';
};

export const formatNumbersDisplayOnly = (value: any) => {
  if (!value) {
    return value;
  }
  const hasSeparator = `${value}`.includes('.');
  const [before, after] = `${value}`.split('.');

  let formattedBefore = '';
  let formattedAfter = '';

  if (before !== '-') {
    formattedBefore = new Intl.NumberFormat().format(+cleanMoney(before));
  }
  if (before === '-') {
    formattedBefore = before;
  }
  if (after !== '' && !isNaN(+after)) {
    let afterFormatted = after;
    if (after.length > 2) {
      afterFormatted = afterFormatted.slice(0, 2);
    }
    if (after.length === 1) {
      afterFormatted = afterFormatted + '0';
    }
    const afterString = '0.' + afterFormatted;
    formattedAfter = afterString;
  }
  if (formattedAfter) {
    return `${formattedBefore}${formattedAfter.slice(1)}`;
  }
  if (hasSeparator) {
    return `${formattedBefore}.`;
  }
  return formattedBefore ?? '';
};

export const renderHTML = (rawHTML: string) =>
  React.createElement('div', {
    dangerouslySetInnerHTML: { __html: decodeHtml(rawHTML) },
  });

function decodeHtml(html: string) {
  const txt = document.createElement('textarea');
  txt.innerHTML = html;
  return txt.value;
}
